import {
  DenmarkPublish,
  FacebookPublish,
  GenericSocialPublish,
  YoutubePublish,
} from "@/database";
import { ProductModel } from "@/database";
import { PresetListModel } from "@/database";
import { AdminPreset } from "@/database";
import { AllAdminPresets } from "@/database";
import { AdminSocialPreset } from "@/database";
import { MediaModel } from "@/database";
import { PRESET_MODES, POLICY_OPTIONS } from "@/utils/constants/uiConstants";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import type { MediaDetailsForm } from "@/routes/pages/MediaDetail/MediaDetailsForm";

// Function to generate options for organizations

export const generateBusinessOptions = (businessProductMap: any) => {
  return Object.keys(businessProductMap).map((key) => ({
    value: key,
    label: formatEntityName(businessProductMap[key]),
  }));
};

export const generateTeamOptions = (
  businessProductMap: any,
  selectedBusiness: any
) => {
  if (!selectedBusiness) {
    return [{ value: "", label: "Select Business first" }];
  }

  const businessUnits = businessProductMap[selectedBusiness]?.data || [];
  return businessUnits.map((businessUnit: any) => ({
    value: businessUnit.id,
    label: formatEntityName(businessUnit),
  }));
};

export const getUqinueTeamFromPermission = (permissionArray: any) => {
  const teamIds = permissionArray?.map((item: any) => {
    const parts = item?.split(".");
    return parts[1]; // 2nd part = teamId
  });

  return [...new Set(teamIds)]; // remove duplicates
};

export const generateTeamBusinessMappedOptions = (
  businessProductMap: any,
  selectedBusiness: any
) => {
  return businessProductMap[selectedBusiness]?.data.map(
    (businessUnit: any) => ({
      value: businessUnit.id,
      label: businessUnit.name,
    })
  );
};

type ListType = FacebookPublish[] | DenmarkPublish[] | YoutubePublish[];

//Function to Generate Publish List
export const generatePublishList = (publishList: ListType) => {
  return [
    ...publishList.map((item) => {
      return {
        platformName: item.platform,
        channelName: "123",
        publishId: "123",
        link: "123",
      };
    }),
  ];
};

export const parseFilterFromUrl = (
  url: string,
  key: string,
  displayValue: string
) => {
  try {
    const urlObj = new URL(url);
    const agencyParams = urlObj.searchParams.getAll(key);
    const agencyDisplayValue = urlObj.searchParams.getAll(displayValue);

    if (agencyParams.length > 0 && agencyDisplayValue.length > 0) {
      // Decode the display value (handles URL encoding like %2C for comma)
      // Create array of objects with label and value
      const agencyArray = agencyParams.map((agencyId, index) => ({
        name: agencyDisplayValue[index] || agencyId, // fallback to agencyId if no display value
        id: agencyId,
      }));

      return agencyArray;
    }

    return [];
  } catch {
    return [];
  }
};

/** Total tag character budget (matches TagsChipInput + media details Yup validation). */
export const TAGS_TOTAL_CHARACTER_LIMIT = 500;
export const TAGS_TOTAL_LENGTH_ERROR_MESSAGE = "Maximum Limit Reached";
export const hasNonEnglishAlphabeticChars = (text: string): boolean => {
  // Check if there are any non-ASCII characters that are letters
  // We allow special characters like , + = and other punctuation/numbers/spaces
  // But we want to detect alphabetic characters from other languages
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charCode = char.charCodeAt(0);

    // Skip ASCII characters (0-127) - these include English letters, numbers, punctuation
    if (charCode <= 127) continue;

    // Check if this non-ASCII character is a letter (using Unicode property)
    if (/\p{L}/u.test(char)) {
      return true; // Found a non-English alphabetic character
    }
  }

  return false;
};

export const filterPresetsByTeamsAndType = (
  teamsList: ProductModel[],
  presetList: PresetListModel[],
  presetType: any
): any[] => {
  // Normalize team IDs
  const teamIds = new Set(
    teamsList.map((team: ProductModel) =>
      typeof team === "string" ? team : team.id
    )
  );

  return presetList.filter(
    (preset: PresetListModel) =>
      preset.type === presetType && !!preset.team && teamIds.has(preset.team)
  );
};

/**
 * Flattens a media's Denmark socials into a flat one-entry-per-section list,
 * expanding each parent's embedded `multi_publish_targets` into standalone
 * sibling entries. `usePresetManagement.applyPresetData` expects
 * `preset.denmark` to already be flat (first entry = parent, rest = children)
 * and rebuilds `multi_publish_targets` itself via its own `toChildTarget` -
 * so a target left nested inside the parent here would never become its own
 * visible chip, and would be dropped once `applyPresetData` overwrites the
 * parent's `multi_publish_targets` from the (non-flattened) list.
 */
const flattenDenmarkSocials = (
  socials: MediaModel["socials"] | undefined
): DenmarkPublish[] => {
  const denmarkParents = (socials || []).filter(
    (social: any) => social?.job_type === "denmark"
  ) as unknown as DenmarkPublish[];

  const flattened: DenmarkPublish[] = [];
  denmarkParents.forEach((parent) => {
    const { multi_publish_targets, ...parentRest } = parent as any;
    flattened.push(parentRest);
    (multi_publish_targets || []).forEach((target: any) => {
      flattened.push({
        ...target,
        job_type: "denmark",
        platform: "denmark",
        channel_name: target?.channel_name ?? "",
        section: target?.section,
        host: target?.host,
        folder_path: target?.folder_path ?? "",
        _parentSectionId: parent.section,
      } as DenmarkPublish);
    });
  });
  return flattened;
};

/**
 * Maps a MediaModel (as returned by media.list) into an AdminPreset-shaped
 * object so it can be fed into the existing "select preset" pipeline
 * (SelectPreset -> handleSelectPreset -> CreateMetaPreset preview -> applyPresetData)
 * as if the media's own metadata were a preset.
 *
 * MediaModel stores contributor/credit fields flat with singular names
 * (e.g. producer, imagecredit) while AdminPreset nests them under
 * contributors/credits with plural names (e.g. contributors.producers,
 * credits.image_credits) - this reconciles that mismatch.
 */
export const mapMediaToPresetPayload = (media: MediaModel): AdminPreset => {
  // The media's own Denmark destinations live in `media.socials` (job_type
  // "denmark"), not in an `on_platform_distribution` field (MediaModel has
  // none) - carry them through (flattened) so CreateMetaPreset's Denmark
  // section can show them like it does for a real preset's denmark_sections.
  const denmarkSections = flattenDenmarkSocials(media?.socials);

  return {
    id: media?.id,
    name: media?.title,
    team: media?.team ? [media.team] : [],
    status: media?.status,
    media_status: media?.media_status,
    usage_count: 0,

    title_prefix: "",
    title_suffix: media?.title,
    description_prefix: "",
    description_suffix: media?.desc,

    agency: media?.agency,
    agency_credits: media?.agency_credits || [],

    ctype: media?.ctype,
    production_type: media?.production_type,
    language: media?.lang,
    category: media?.category,
    sub_category: media?.subcategory,

    tags: media?.tags || [],
    meta_team: media?.team,
    content_rating: media?.mfk,

    contributors: {
      producers: media?.producer || [],
      editors: media?.editor || [],
      anchors: media?.anchor || [],
      photographers: media?.photographer || [],
      researcher: media?.researcher || [],
      compiler: media?.compiler || [],
      curator: media?.curator || [],
      reporter: media?.reporter || [],
      author: media?.author || [],
      script_writers: media?.script_writer || [],
      cameramen: media?.cameraman || [],
    },
    credits: {
      image_credits: media?.imagecredit || [],
      video_credits: media?.videocredit || [],
      audio_credits: media?.audiocredit || [],
    },

    on_platform_distribution: {
      denmark_sections: denmarkSections as any,
      playlists: [],
      live_channels: [],
    },

    // Resolved user/agency info (name, email) for chip-input labels in
    // CreateMetaPreset - only present when `media` came from a media.get
    // call (media.list items don't carry these).
    agencydata: media?.agencydata as any,
    agencyCreditData: media?.agencyCreditData as any,
    userContributorData: media?.userContributorData as any,
    creditsData: media?.creditsData as any,

    // The source media's own social publishes, carried through so
    // CreateMetaPreset can show a "Social Channels" chip per channel and
    // preview it - not a real AdminPreset field, only present here when
    // `media` came from a media.get call.
    socials: media?.socials as any,

    // The source media's own playlist memberships, carried through so
    // CreateMetaPreset can show a "Playlists" chip per playlist - not a real
    // AdminPreset field, only present here when `media` came from a
    // media.get call.
    playlists: (media as any)?.playlists,
    playlistsData: (media as any)?.playlistsData,

    // Thumbnail source fields, carried through so CreateMetaPreset can show
    // the same thumbnail SelectPreset displayed when picking this media.
    images: media?.images,
    cdn: media?.cdn,
    source: media?.source,
    orientation: media?.orientation,
    gcl: media?.gcl,
    gct: media?.gct,
    expiry: media?.expiry,
    youtube_id: media?.youtube_id,
    parentid: media?.parentid,
    // Full parent media record (same shape MediaDetailsForm reads via
    // currentMediaDetailsForEdit.parentData) so CreateMetaPreset can render
    // the same SelectedMediaCard instead of a bare id/title string.
    parentData: (media as any)?.parentData,

    // Marks this payload as sourced from a media (not a real preset), so
    // CreateMetaPreset can adapt its UI - e.g. media has no title/description
    // prefix-suffix split, so hide the Override/Fill-if-empty/Append mode
    // legend and show plain title/description fields instead.
    _isMediaSource: true as any,
  } as unknown as AdminPreset;
};

export const YOUTUBE_TAG_BYTE_MIN = 2;
export const YOUTUBE_TAG_BYTE_MAX = 100;

export const YOUTUBE_SOCIAL_TAG_LENGTH_ERROR_MESSAGE = `Each tag must be between ${YOUTUBE_TAG_BYTE_MIN} and ${YOUTUBE_TAG_BYTE_MAX} bytes`;

export const getByteLength = (text: string): number =>
  new TextEncoder().encode(text).length;

export const isYoutubeTagByteLengthValid = (tag: string): boolean => {
  const trimmed = tag.trim();
  if (trimmed === "") return true;
  const bytes = getByteLength(trimmed);
  return bytes >= YOUTUBE_TAG_BYTE_MIN && bytes <= YOUTUBE_TAG_BYTE_MAX;
};

export const areYoutubeSocialTagsCharLengthValid = (
  tags: string[] | undefined | null
): boolean => {
  if (!tags || !Array.isArray(tags)) {
    return true;
  }
  for (const raw of tags) {
    if (!isYoutubeTagByteLengthValid(String(raw))) {
      return false;
    }
  }
  return true;
};

const isYoutubeSocialPublish = (social: GenericSocialPublish): boolean =>
  social.job_type === "youtube" || social.platform === "youtube";

/**
 * Validates socials array items
 * Only validates items where status === 0
 * Returns false if any item with status === 0 has:
 * - title length > 100, OR
 * - desc length > 5000, OR
 * - tags length > 500, OR
 * - (YouTube only) any non-empty tag outside 2–100 UTF-8 bytes
 * Otherwise returns true
 */
export const validateSocials = (socials: GenericSocialPublish[]): boolean => {
  if (!Array.isArray(socials) || socials.length === 0) {
    return true;
  }

  for (const social of socials) {
    // Only check lengths if status is 0
    if (social.status === 0) {
      // Check if title length is greater than 100
      const titleLength = social.title?.length || 0;
      if (titleLength > 100) {
        return false;
      }

      // Check if desc length is greater than 5000
      const descLength = social.desc?.length || 0;
      if (descLength > 5000) {
        return false;
      }

      // Check if tags length is greater than 500
      if (social.tags && Array.isArray(social.tags)) {
        const { tagCount } = calculateTagCount(
          social.tags,
          isYoutubeSocialPublish(social)
        );
        if (tagCount > 500) {
          return false;
        }
      }

      if (
        isYoutubeSocialPublish(social) &&
        !areYoutubeSocialTagsCharLengthValid(social.tags)
      ) {
        return false;
      }
    }
  }

  return true;
};

export const calculateTagCount = (tags: string[], isYoutube = false) => {
  if (!Array.isArray(tags)) {
    throw new Error("Invalid input: tags should be an array of strings");
  }

  let tagCount = 0;
  const warningTags: string[] = [];
  let nonEmptyTags = 0;

  tags.forEach((tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag === "") {
      return;
    }
    // Calculate UTF-8 byte length
    const bytes = new TextEncoder().encode(trimmedTag).length;
    if (bytes > 100 || bytes < 2) {
      warningTags.push(trimmedTag);
    }

    tagCount += trimmedTag.length;

    if (isYoutube && /\s/.test(trimmedTag)) {
      tagCount += 2;
    }
    nonEmptyTags += 1;
  });
  if (isYoutube && nonEmptyTags > 1) {
    tagCount += nonEmptyTags - 1;
  }

  return { tagCount, warningTags };
};

type PresetModeValue =
  | (typeof PRESET_MODES)[keyof typeof PRESET_MODES]
  | string;

const normalizePresetMode = (
  mode: PresetModeValue | undefined | null,
  defaultMode: PresetModeValue
) => {
  const normalized = mode?.toString().trim().toLowerCase();
  if (normalized === PRESET_MODES.FILL_IF_EMPTY)
    return PRESET_MODES.FILL_IF_EMPTY;
  if (normalized === PRESET_MODES.APPEND) return PRESET_MODES.APPEND;
  if (normalized === PRESET_MODES.OVERRIDE) return PRESET_MODES.OVERRIDE;
  if (normalized === PRESET_MODES.SKIP) return PRESET_MODES.SKIP;
  return defaultMode.toString().trim().toLowerCase();
};

export const resolveTagsByMode = (params: {
  mode?: PresetModeValue | null;
  currentTags?: string[] | null;
  incomingTags?: string[] | null;
  defaultMode?: PresetModeValue;
}): string[] => {
  const {
    mode,
    currentTags: currentTagsInput,
    incomingTags: incomingTagsInput,
    defaultMode = PRESET_MODES.OVERRIDE,
  } = params;

  const currentTags = Array.isArray(currentTagsInput) ? currentTagsInput : [];
  const incomingTags = Array.isArray(incomingTagsInput)
    ? incomingTagsInput
    : [];
  const effectiveMode = normalizePresetMode(mode, defaultMode);

  if (effectiveMode === PRESET_MODES.SKIP) {
    return currentTags;
  }

  if (effectiveMode === PRESET_MODES.FILL_IF_EMPTY) {
    if (currentTags.length === 0 && incomingTags.length > 0)
      return incomingTags;
    return currentTags;
  }

  if (effectiveMode === PRESET_MODES.APPEND) {
    const merged = [
      ...currentTags,
      ...incomingTags.filter((t) => !currentTags.includes(t)),
    ];
    return merged;
  }

  // OVERRIDE or fallback: only override if we actually have incoming tags
  if (incomingTags.length > 0) return incomingTags;
  return currentTags;
};

// Checks contributor_policy of selected product for given keys.
// Returns true if any specified key has a numeric value > 1.
export const hasContributorPolicyValueGreaterThanOne = (
  selectedProduct: ProductModel | null | undefined,
  keys: string[]
): boolean => {
  // Default-allow: if we don't have keys to check or policy is missing,
  // treat it as permitted.
  if (!keys?.length) return true;
  const contributor_policy = (selectedProduct as any)?.contributor_policy as
    | Record<string, unknown>
    | undefined
    | null;
  if (!selectedProduct || !contributor_policy) return true;

  return keys.some((key) => {
    const value = contributor_policy?.[key];
    // If the key is not present in the policy, default to true.
    if (value === undefined || value === null) return true;
    return typeof value === "number" && value > 1;
  });
};

// Checks credits_policy of selected product for given keys.
// Returns true if any specified key has a numeric value > 1.
export const hasCreditsPolicyValueGreaterThanOne = (
  selectedProduct: ProductModel | null | undefined,
  keys: string[]
): boolean => {
  // Default-allow: if we don't have keys to check or policy is missing,
  // treat it as permitted.
  if (!keys?.length) return true;
  const credits_policy = (selectedProduct as any)?.credits_policy as
    | Record<string, unknown>
    | undefined
    | null;
  if (!selectedProduct || !credits_policy) return true;

  return keys.some((key) => {
    const value = credits_policy?.[key];
    // If the key is not present in the policy, default to true.
    if (value === undefined || value === null) return true;
    return typeof value === "number" && value > 1;
  });
};

// Checks geo_policy of selected product for given keys.
// Returns true if any specified key has a numeric value > 1.
export const hasGeoPolicyValueGreaterThanOne = (
  selectedProduct: ProductModel | null | undefined,
  keys: string[]
): boolean => {
  // Default-allow: if we don't have keys to check or policy is missing,
  // treat it as permitted.
  if (!keys?.length) return true;
  const geo_policy = (selectedProduct as any)?.geo_policy as
    | Record<string, unknown>
    | undefined
    | null;
  if (!selectedProduct || !geo_policy) return true;

  return keys.some((key) => {
    const value = geo_policy?.[key];
    // If the key is not present in the policy, default to true.
    if (value === undefined || value === null) return true;
    return typeof value === "number" && value > 1;
  });
};

// Generic checker across contributor/credits/geo policies.
// `type` decides which policy to check on the selected product.
export type ProductPolicyType = "contributor" | "credits" | "geo";

export const hasPolicyValueGreaterThanOne = (
  type: ProductPolicyType,
  selectedProduct: ProductModel | null | undefined
): boolean => {
  switch (type) {
    case "contributor":
      return hasContributorPolicyValueGreaterThanOne(selectedProduct, [
        "producer",
        "anchor",
        "editor",
        "script_writer",
        "cameraman",
        "photographer",
        "researcher",
        "compiler",
        "curator",
        "reporter",
        "author",
      ]);
    case "credits":
      return hasCreditsPolicyValueGreaterThanOne(selectedProduct, [
        "imagecredit",
        "videocredit",
        "audiocredit",
      ]);
    case "geo":
      return hasGeoPolicyValueGreaterThanOne(selectedProduct, ["gcl", "gct"]);
    default:
      return false;
  }
};

export const filterAdminPresetsByTeamsAndType = (
  teamsList: ProductModel[],
  presetList: AllAdminPresets,
  presetType: any
): any[] => {
  // Normalize team IDs
  const teamIds = new Set(
    teamsList.map((team: ProductModel) =>
      typeof team === "string" ? team : team.id
    )
  );

  return (presetList as any)[presetType]?.filter(
    (preset: AdminPreset | AdminSocialPreset) =>
      !!preset.team && teamIds.has(preset.team)
  );
};

export function convertChannelConfigToSocial(payload: any): any {
  return {
    id: payload?.id,
    nid: payload?.nid,
    name: payload?.name,
    status: payload?.status,
    team: payload?.team,
    type: "social",

    ct: payload?.ct,
    ctby: payload?.ctby,
    mt: payload?.mt,
    mtby: payload?.mtby,

    social: payload?.channel_configs?.map(({ channel_id, ...rest }: any) => ({
      platform: channel_id, // channel_id ➜ platform
      // No `title`/`desc` here - a channel_configs entry only carries
      // title_prepend/title_append/description_prepend/description_append
      // (via ...rest below), not a fully-composed title/desc. Faking one
      // from title_append/description.append (the latter doesn't even
      // exist on ChannelConfigs, which is flat) used to make callers treat
      // this preset as already-authored content and skip the real
      // prepend+mediaTitle+append composition in BasePublishCard.
      title_prepend: rest?.title_prepend || "",
      title_append: rest?.title_append || "",
      description_prepend: rest?.description_prepend || "",
      description_append: rest?.description_append || "",
      tags: rest?.tags || [],
      tags_mode: rest?.tags_mode || "append",
      privacy: rest?.privacy,
      channel_name: rest?.channel_name,
      status: 0,
      monetize_policy_id: rest?.monetize_policy_id,
      is_created_for_kids: rest?.is_created_for_kids,
      is_rights_managements_allowed:
        rest?.is_rights_managements_allowed || "no",
      job_type: rest?.job_type,
      media: rest?.media || "",
      playlist: Array.isArray(rest?.playlist)
        ? rest.playlist.join(",")
        : rest?.playlist || "",
      scheduling_type: rest?.scheduling_type,
      category: rest?.category,
      title_ai_type: rest?.title_ai_transform,
      title_ai_value: rest?.title_custom_prompt,
      desc_ai_type: rest?.description_ai_transform,
      desc_ai_value: rest?.description_custom_prompt,
      isFromPreset: true,
      schedule_after:
        rest?.privacy === "schedule"
          ? convertToScheduleAfterForRelativeUnit(
              rest?.relative_unit,
              rest?.relative_delay
            )
          : 0,
      ...rest,
    })),
  };
}

/**
 * Converts a media item's own `socials` (as returned by media.get) into the
 * same `SocialPresetModel[]`-shaped array a publish preset's `.social` field
 * carries, so it can be fed into `AllSocialPublish`'s `presetDetails` prop to
 * pre-open/pre-select the matching social channel sections - the same flow
 * used today when a publish preset is selected on the Social Publish page.
 *
 * MediaModel.socials entries already use matching field names (channel_name,
 * job_type, title, desc, tags, privacy, ...), so this only needs to drop
 * publish-identity fields (id/published_id/status) that belong to the
 * source media, not the media currently being edited, and mark each entry
 * isFromPreset so it's treated as a fresh suggestion rather than an
 * already-published record.
 */
export const mediaSocialsToPresetSocialPayload = (
  socials: any[] | undefined | null
): any[] => {
  if (!Array.isArray(socials)) return [];

  return socials
    .filter((social) => social?.channel_name && social?.job_type)
    .map((social) => {
      const {
        id: _id,
        published_id: _publishedId,
        routing_id: _routingId,
        status: _status,
        msg: _msg,
        ...rest
      } = social;

      return {
        ...rest,
        status: 0,
        isFromPreset: true,
      };
    });
};

const convertToScheduleAfterForRelativeUnit = (
  relative_unit: string,
  relative_delay: number
) => {
  if (relative_unit === "hours") {
    return relative_delay * 60;
  }
  if (relative_unit === "days") {
    return relative_delay * 24 * 60;
  }
  return relative_delay;
};

export const anyVisibleRightSection = (selectedTeam: ProductModel) => {
  if (selectedTeam?.expiry_policy !== 1) return true;
  if (selectedTeam?.youtube_id_policy !== 1) return true;
  if (selectedTeam?.parentid_policy !== 1) return true;
  return false;
};

/**
 * Returns the scheduled epoch timestamp (ms) based on schedule_type.
 * - DYNAMIC: now + schedule_after minutes converted to ms
 * - CUSTOM: returns the schedule epoch value directly
 */
export const getScheduleTime = ({
  schedule_type,
  schedule_after,
  schedule,
}: {
  schedule_type: "DYNAMIC" | "CUSTOM" | string;
  schedule_after?: number;
  schedule?: number;
}): number | undefined => {
  if (schedule_type === "DYNAMIC") {
    if (schedule_after == null) return 0;
    return Date.now() + schedule_after * 60 * 1000;
  }
  if (schedule_type === "CUSTOM") {
    return schedule;
  }
  return 0;
};

export const deduplicateContributorOptions = (options: any[]) => {
  const seen = new Map();
  for (const opt of options || []) {
    if (!seen.has(opt.value)) {
      seen.set(opt.value, opt);
    }
  }
  return Array.from(seen.values());
};

/**
 * Builds the `denmark` array for the media-update API payload.
 *
 * Handles three kinds of items:
 *  - Top-level draft/new sections (parents without a `_parentSectionId`).
 *  - Children of already-published parents: the published parent is
 *    reconstructed and the child is embedded in its `multi_publish_targets`.
 *  - Denmark-embed articles (passed through as-is from formik values).
 */
export const buildDenmarkPayload = (
  values: any,
  currentSelectedSection: DenmarkPublish[],
  publishedSocials: any[],
  formikDenmarkValues: DenmarkPublish[]
): any[] => {
  // Children whose parent is already published in the backend
  const childrenOfPublished = currentSelectedSection.filter(
    (item) =>
      item.job_type === "denmark" &&
      item._parentSectionId &&
      publishedSocials.some(
        (s) => s.section === item._parentSectionId && s.job_type === "denmark"
      )
  );

  // Top-level new/draft items (parents; not children of a published parent)
  const topLevelItems = currentSelectedSection.filter(
    (item) => item.job_type === "denmark" && !item._parentSectionId
  );

  const mapDenmarkItem = (item: any) => ({
    section: item.section,
    channel_name: item.channel_name,
    host: item.host,
    folder_path: item.folder_path,
    article_type: "api",
    article_category: "denmark",
    schedule: getScheduleTime({
      schedule_type: item?.schedule_type,
      schedule_after: item?.schedule_after,
      schedule: item?.schedule,
    }),
    schedule_type: item?.schedule_type,
    schedule_after: item?.schedule_after,
    taxonomy: item?.taxonomy,
    denmark_tags: Array.isArray(values?.tags ?? item?.seotags)
      ? (values?.tags ?? item?.seotags)
      : (values?.tags ?? item?.tags ?? []),
    activationStatus: item?.activationStatus,
    denmark_title: values?.title ?? item?.title,
    denmark_description: values?.desc ?? item?.desc ?? item?.description,
    denmark_privacy: item?.privacy ?? item?.activationStatus ?? "ACTIVE",
    title_for_url: item?.title_for_url,
    multi_publish_targets: item?.multi_publish_targets,
  });

  // For each published parent that has new children, reconstruct it with
  // updated multi_publish_targets so the API picks up the relationship.
  const uniqueParentIds = [
    ...new Set(childrenOfPublished.map((c) => c._parentSectionId as number)),
  ];

  const publishedParentEntries = uniqueParentIds
    .map((parentSectionId) => {
      const publishedParent = publishedSocials.find(
        (s) => s.section === parentSectionId && s.job_type === "denmark"
      );
      if (!publishedParent) return null;

      const children = childrenOfPublished.filter(
        (c) => c._parentSectionId === parentSectionId
      );
      const newMultiTargets = children.map((child) => ({
        section: child.section,
        parent_id: child.section,
        channel_name: child.channel_name,
        host: child.host,
        folder_path: child.folder_path,
        open_story_at: "PUBLISH_SECTION",
        open_newly_in_browser: false,
        isOverRideLinkRequired: true,
        contentPublishType: "MULTIPUBLISH",
      }));

      // Merge with targets that already exist on the published parent so that
      // previously saved children are not silently dropped when the user adds
      // one more child in the same save cycle.
      const existingTargets: any[] = Array.isArray(
        publishedParent.multi_publish_targets
      )
        ? publishedParent.multi_publish_targets
        : [];
      const newSections = new Set(newMultiTargets.map((t) => t.section));
      const mergedMultiTargets = [
        ...existingTargets.filter((t) => !newSections.has(t.section)),
        ...newMultiTargets,
      ];

      return {
        ...mapDenmarkItem(publishedParent),
        multi_publish_targets: mergedMultiTargets,
      };
    })
    .filter(Boolean);

  const embedEntries = Array.isArray(formikDenmarkValues)
    ? formikDenmarkValues
        .filter((item) => item?.job_type === "denmark_embed")
        .map((item: any) => ({
          section: item.section,
          channel_name: item.channel_name,
          host: item.host,
          folder_path: item.folder_path || "",
          article_type: item.upload_type || "lead",
          article_category: "denmark_embed",
        }))
    : [];

  return [
    ...topLevelItems.map(mapDenmarkItem),
    ...publishedParentEntries,
    ...embedEntries,
  ];
};

// Returns 0-3 based on how well `label` matches `q` (both already lowercased)
const basicScore = (label: string, q: string): number => {
  const l = label?.toLowerCase() ?? "";
  const name = l.split("(")[0].trim();
  if (l === q || name === q) return 0;
  if (l.startsWith(q) || name.startsWith(q)) return 1;
  if (l.includes(q) || name.includes(q)) return 2;
  return 3;
};

// Sort: exact → starts-with → contains → rest (case-insensitive)
export const sortOptionsByBasicMatch = <T extends { label?: string }>(
  opts: T[],
  keyword: string
): T[] => {
  if (!keyword) return opts;
  const q = keyword.toLowerCase();
  return [...opts].sort(
    (a, b) => basicScore(a.label ?? "", q) - basicScore(b.label ?? "", q)
  );
};

// Sort: most consecutive keyword characters matched first, tie-broken by basicScore
export const sortOptionsByFuzzyMatch = <T extends { label?: string }>(
  opts: T[],
  keyword: string
): T[] => {
  if (!keyword) return opts;
  const q = keyword.toLowerCase();
  const matchedChars = (label: string): number => {
    const l = label?.toLowerCase() ?? "";
    const name = l.split("(")[0].trim();
    const target = name || l;
    let qi = 0;
    for (let i = 0; i < target.length && qi < q.length; i++) {
      if (target[i] === q[qi]) qi++;
    }
    return qi;
  };
  return [...opts].sort((a, b) => {
    const diff = matchedChars(b.label ?? "") - matchedChars(a.label ?? "");
    if (diff !== 0) return diff;
    return basicScore(a.label ?? "", q) - basicScore(b.label ?? "", q);
  });
};

// ─── Media Update Payload Builder ────────────────────────────────────────────

/**
 * Simple 1-to-1 diff fields: sent only when the value changed vs initialValues.
 * Key = form field name = payload key.
 */
const DIFF_FIELDS: ReadonlyArray<keyof MediaDetailsForm> = [
  "desc",
  "tags",
  "denmark_keywords",
  "meta_lang",
  "category",
  "subcategory",
  "business",
  "team",
  "agency",
  "agency_credits",
  "asset_type",
  "media_ref",
  "producer",
  "editor",
  "script_writer",
  "anchor",
  "cameraman",
  "videocredit",
  "audiocredit",
  "imagecredit",
  "ctype",
  "production_type",
  "campaign_id",
  "parentid",
  "youtube_id",
  "expiry",
  "smeta",
  "gcl",
  "gct",
  "mfk",
  "schedule",
  // Premiere-specific
  "sourceInput",
  "media_schedule",
  "playback_mode",
  "loop_count",
  // Denmark-host contributors (policy-gated ones handled separately below)
  "researcher",
  "compiler",
  "curator",
  "reporter",
  "author",
  "category_tags",
] as const;

/** Image-related form fields that map into the composite `images` payload object. */
const IMAGE_FIELDS: ReadonlyArray<keyof MediaDetailsForm> = [
  "landscapeThumbId",
  "posterThumbId",
  "socialThumbId",
  "portraitThumbId",
  "podcastThumbId",
  "premiereThumbId",
  "premiereVThumbId",
  "is_social_change",
] as const;

function hasChanged(
  init: MediaDetailsForm,
  values: MediaDetailsForm,
  key: keyof MediaDetailsForm
): boolean {
  const a = init[key];
  const b = values[key];
  if (Array.isArray(a) && Array.isArray(b))
    return JSON.stringify(a) !== JSON.stringify(b);
  if (
    a !== null &&
    b !== null &&
    typeof a === "object" &&
    typeof b === "object"
  )
    return JSON.stringify(a) !== JSON.stringify(b);
  return a !== b;
}

export interface BuildMediaUpdatePayloadParams {
  values: MediaDetailsForm;
  initialValues: MediaDetailsForm;
  mediaId: string;
  currentMediaState: number | undefined;
  currentState: number | undefined;
  currentStatus: number | undefined;
  currentImages: Record<string, any> | undefined;
  currentSimulive: any;
  simuliveData: any;
  currentDenmark: DenmarkPublish[];
  currentSelectedSection: DenmarkPublish[];
  isSaveAndPublishClicked: boolean;
  userEditedTitleUrl: boolean;
  currentTitleUrl: string | undefined;
  currentTitleUrlEdited: string | undefined;
  isDefaultHost: boolean;
  photographerPolicy: number | undefined;
  socials: any[];
}

export function buildMediaUpdatePayload({
  values,
  initialValues,
  mediaId,
  currentMediaState,
  currentState,
  currentStatus,
  currentImages,
  currentSimulive,
  simuliveData,
  currentDenmark,
  currentSelectedSection,
  isSaveAndPublishClicked,
  userEditedTitleUrl,
  currentTitleUrl,
  currentTitleUrlEdited,
  isDefaultHost,
  photographerPolicy,
  socials,
}: BuildMediaUpdatePayloadParams): Record<string, any> {
  const changed = (key: keyof MediaDetailsForm) =>
    hasChanged(initialValues, values, key);

  const isPublishing =
    isSaveAndPublishClicked &&
    currentMediaState !== 1 &&
    Number(values.media_state) === 1;

  const imagesChanged = IMAGE_FIELDS.some((f) => changed(f));

  const denmarkChanged =
    JSON.stringify(currentDenmark) !== JSON.stringify(currentSelectedSection) ||
    changed("denmark");

  const payload: Record<string, any> = {
    // Always sent
    id: mediaId,
    title: values.title,
  };

  // title also drives stitle
  if (changed("title")) {
    payload.stitle = [values.title];
  }

  // lang has a fallback default
  if (changed("lang")) {
    payload.lang = values.lang || "english";
  }

  // title_url bundles two derived fields; also send both, falling back to
  // title, if either was missing at the source (ES), even when unchanged in the form.
  if (changed("title_url") || !currentTitleUrl || !currentTitleUrlEdited) {
    payload.title_url = values.title_url || values.title;
    payload.is_title_url_edited = userEditedTitleUrl;
    payload.title_url_edited = values.title_url_edited || values.title;
  }

  // meta
  if (changed("meta")) {
    payload.meta = values.meta;
  }

  // images: composite object, only when any image field changed
  if (imagesChanged) {
    payload.images = {
      base: currentImages?.base,
      thumb: values.landscapeThumbId,
      poster: values.posterThumbId,
      social: values.socialThumbId,
      portrait: values.portraitThumbId,
      podcast: values.podcastThumbId,
      premiere: values.premiereThumbId,
      premiere_v: values.premiereVThumbId,
      is_social_change: values.is_social_change,
    };
  }

  // simulive: only when media has simulive and it changed
  if (currentSimulive && changed("simulive")) {
    payload.simulive = simuliveData;
  }

  // denmark: only when sections or denmark values changed
  if (denmarkChanged) {
    payload.is_denmark =
      currentSelectedSection.length > 0 ||
      (values.denmark && values.denmark.length > 0);
    payload.denmark = buildDenmarkPayload(
      values,
      currentSelectedSection,
      socials,
      values.denmark || []
    );
  }

  // media_state + pub_ts: only when transitioning to published
  if (isPublishing) {
    payload.media_state = 1;
    if (currentState === 10) {
      payload.pub_ts = Date.now();
    }
  }

  if (currentState === -99) {
    payload.state = 10;
  }
  if (currentStatus === -99) {
    payload.status = 5;
  }

  // Simple 1-to-1 diff fields
  for (const key of DIFF_FIELDS) {
    if (changed(key)) {
      payload[key] = values[key];
    }
  }

  // photographer is conditional on policy
  if (
    isDefaultHost &&
    changed("photographer") &&
    photographerPolicy !== POLICY_OPTIONS.HIDE
  ) {
    payload.photographer = values.photographer;
  }

  return payload;
}

export function resolveThumbIdByOrientation(
  media:
    | {
        images?: Record<string, any>;
        orientation?: number;
      }
    | undefined
): string | undefined {
  if (media?.images?.social) return media.images.social;
  if (media?.orientation === 1) return media?.images?.thumb;
  if (media?.orientation === 2) return media?.images?.portrait;
  if (media?.orientation === 3) return media?.images?.landscape;
  return media?.images?.thumb || media?.images?.portrait;
}
